"use client";

import { motion, useTransform } from "framer-motion";

import { useProfitSplit } from "@/components/global/ProfitSplitProvider";
import type { PageHomeJourneyStep } from "@/sanity/pages/PageHomeTemplate";
import JourneyMediaFrame, { Artwork, u, type JourneyTimeline } from "./JourneyMediaFrame";
import { journeyAmount, journeyCopy } from "./journeyMediaCopy";
import styles from "./journeyMedia.module.scss";
import funding from "./journeyFunding.module.scss";

const DAYS = [26, 27, 28, 29, 30, 31, ...Array.from({ length: 31 }, (_, i) => i + 1)];
const DURATIONS = [1400, 950, 950, 950, 1500, 7600];
const PAYOUTS = [7200, 1800, 900, 450];
const clamp = (value: number) => Math.max(0, Math.min(1, value));
const easeOut = (value: number) => 1 - (1 - clamp(value)) ** 3;

function TradingDay({ day, position, index, timeline }: { day: number; position: number; index: number; timeline: JourneyTimeline }) {
  const dayIndex = position - 8;
  const traded = dayIndex >= 0 && dayIndex <= Math.min(index, 4);
  const current = dayIndex === index;
  const fill = useTransform(timeline.progress, (progress) => traded ? (current ? easeOut(progress / 0.4) : 1) : 0);
  const background = useTransform(fill, (value) => `rgba(143,237,184,${0.06 + value * (current ? 0.18 : 0.06)})`);
  const borderColor = useTransform(fill, (value) => `rgba(143,237,184,${0.08 + value * (current ? 0.55 : 0.14)})`);
  const checkLength = useTransform(fill, (value) => clamp((value - 0.3) / 0.7));
  const checkOpacity = useTransform(fill, (value) => value > 0.3 ? 1 : 0);

  return (
    <motion.span data-previous={position < 6} className={funding.day} style={{ background, borderColor }}>
      {day}
      {traded && <motion.svg className={funding.dayCheck} viewBox="0 0 12 8" fill="none" style={{ opacity: checkOpacity }}><motion.path d="m1 4 3 3 7-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ pathLength: checkLength }} /></motion.svg>}
    </motion.span>
  );
}

function PayoutNotification({ value, order, index, timeline, label, locale }: { value: number; order: number; index: number; timeline: JourneyTimeline; label: string; locale: string }) {
  const arrival = 0.19 + order * 0.105;
  const entrance = useTransform(timeline.progress, (progress) => timeline.reducedMotion ? 1 : index === 5 ? easeOut((progress - arrival) / 0.12) : 0);
  const transform = useTransform(entrance, (value) => `translateY(calc(var(--u) * ${-22 * (1 - value)})) scale(${0.94 + value * 0.06})`);
  const opacity = useTransform(entrance, (value) => value * (1 - order * 0.18));
  const amount = useTransform(entrance, (progress) => `+$${journeyAmount(Math.round(value * progress), locale)}`);

  return (
    <motion.div className={`${styles.notification} ${funding.notification}`} style={{ transform, opacity }}>
      <span className={styles.appIcon}><Artwork name="logo-white.svg" /></span>
      <div><span>IQ Funded</span><span>{label}</span></div>
      <motion.span className={styles.payout}>{amount}</motion.span>
    </motion.div>
  );
}

function FundingScene({ step, language, index, timeline }: { step: PageHomeJourneyStep; language?: string; index: number; timeline: JourneyTimeline }) {
  const copy = journeyCopy(language);
  const split = useProfitSplit();
  const calendarOpacity = useTransform(timeline.progress, (progress) => timeline.reducedMotion || index === 5 ? 0 : index === 4 ? 1 - easeOut((progress - 0.58) / 0.42) : index === 0 ? easeOut(progress / 0.18) : 1);
  const calendarTransform = useTransform(timeline.progress, (progress) => {
    const exit = index === 4 ? easeOut((progress - 0.58) / 0.42) : index === 5 ? 1 : 0;
    const entrance = index === 0 ? 1 - easeOut(progress / 0.3) : 0;
    return `translateY(calc(var(--u) * ${entrance * 22 - exit * 32})) scale(${1 - exit * 0.045})`;
  });
  const account = useTransform(timeline.progress, (progress) => `$${journeyAmount(Math.round((step.mediaAccountSize ?? 200000) * (index === 0 ? easeOut(progress / 0.7) : 1)), copy.locale)}`);
  const phoneOpacity = useTransform(timeline.progress, (progress) => timeline.reducedMotion ? 1 : index === 5 ? 1 - easeOut((progress - 0.92) / 0.08) : index === 4 ? easeOut((progress - 0.78) / 0.22) : 0);
  const phoneTransform = useTransform(timeline.progress, (progress) => {
    if (timeline.reducedMotion) return "none";
    const offset = index === 5 ? 320 * (1 - easeOut(progress / 0.22)) + 28 * easeOut((progress - 0.92) / 0.08) : index === 4 ? 500 - 180 * easeOut((progress - 0.78) / 0.22) : 500;
    return `translateY(calc(var(--u) * ${offset}))`;
  });
  const ringsOpacity = useTransform(timeline.progress, (progress) => timeline.reducedMotion ? 1 : index === 5 ? easeOut(progress / 0.28) * (1 - easeOut((progress - 0.92) / 0.08)) : 0);

  return (
    <>
      <motion.div className={funding.calendarStage} style={{ opacity: calendarOpacity, transform: calendarTransform }}>
        {[286, 308, 328].map((width, i) => <div key={width} className={styles.calendarLayer} style={{ width: u(width), top: u(25 + i * 9.5), height: u(74 - i * 9.5) }} />)}
        <div className={styles.calendar}>
          <div className={styles.calendarHeader}>
            <Artwork name="logo-white.svg" /><span>IQ Funded</span>
            <span className={`${styles.fundedAmount} ${funding.account}`}><span>{copy.upTo}</span><motion.span>{account}</motion.span></span>
            <span className={styles.split}>{`${copy.upTo} ${split ?? 90}%`}</span>
          </div>
          <div className={styles.days}>
            {DAYS.map((day, position) => position >= 8 && position <= 12
              ? <TradingDay key={position} day={day} position={position} index={index} timeline={timeline} />
              : <span key={position} data-previous={position < 6}>{day}</span>)}
          </div>
        </div>
      </motion.div>
      <motion.div className={funding.rings} style={{ opacity: ringsOpacity }}><Artwork name="payout-rings.svg" className={styles.background} /></motion.div>
      <motion.div className={funding.phoneStage} style={{ opacity: phoneOpacity, transform: phoneTransform }}>
        <div className={styles.phone}>
          <div className={styles.phoneBezel} />
          <div className={styles.phoneScreen}><span className={styles.clock}>09:41</span></div>
          <div className={styles.status}><span>9:41</span><span className={styles.signal}><i /><i /><i /><i /></span><Artwork name="wifi.svg" /><span className={styles.battery} /></div>
          <div className={styles.camera}>
            <i><Artwork name="lens-3.svg" /><Artwork name="lens-4.svg" /></i>
            <i><Artwork name="lens-1.svg" /><Artwork name="lens-2.svg" /></i>
          </div>
        </div>
        <div className={styles.notifications}>
          {PAYOUTS.map((value, order) => <PayoutNotification key={value} value={value} order={order} index={index} timeline={timeline} label={step.mediaPayoutLabel || copy.payout} locale={copy.locale} />)}
        </div>
      </motion.div>
    </>
  );
}

export default function JourneyFundingMedia({ step, language, className }: { step: PageHomeJourneyStep; language?: string; className?: string }) {
  return (
    <JourneyMediaFrame variant="funding" language={language} length={6} durations={DURATIONS} className={className}>
      {(index, timeline) => <FundingScene step={step} language={language} index={index} timeline={timeline} />}
    </JourneyMediaFrame>
  );
}
